import { marketShareVendorLabel } from '@/lib/procurementCompetitors';
import { toDisplayCountryName } from '@/utils/countryCentroids';

/** Normalize aggregate country keys to display labels (e.g. US/USA → United States). */
export function normalizeAggregateCountryKey(country) {
  return toDisplayCountryName(country) || String(country || '').trim() || 'Unknown';
}

/** API by_country row → map / summary shape used by CompetitorIntelligence. */
export function mapAggregateByCountry(byCountry = []) {
  return (byCountry || []).map((row) => ({
    country: normalizeAggregateCountryKey(row.country),
    accounts: row.account_count ?? row.deal_count ?? 0,
    zeroToTwelve: (row.dw_0_6 ?? 0) + (row.dw_6_12 ?? 0),
    sentiment: {
      neg: row.sent_negative ?? 0,
      neu: row.sent_neutral ?? 0,
      pos: row.sent_positive ?? 0,
      unk: row.sent_unknown ?? 0,
    },
    sentimentByVendor: { neg: {}, neu: {}, pos: {}, unk: {} },
    byCompetitor: {},
    /** USD total for this country (actuals from BE). */
    totalValue: Number(row.total_value_usd) || 0,
    /** Optional: actual + NEO fallback total when BE sends it. */
    totalValueInclEst: Number(row.total_value_incl_est_usd) || 0,
  }));
}

export function buildSummaryFromAggregate(aggregate, mergedVendors) {
  const totals = aggregate?.totals || {};
  const top3Vendors = (mergedVendors || [])
    .slice()
    .sort((a, b) => b.deal_count - a.deal_count)
    .slice(0, 3)
    .map((v) => ({ name: v.vendor, valueUSD: 0, dealCount: v.deal_count ?? 0 }));

  const totalValueInclEst =
    totals.total_value_incl_est_usd != null ? Number(totals.total_value_incl_est_usd) : null;
  const totalValueUsd = totals.total_value_usd != null ? Number(totals.total_value_usd) : null;

  return {
    totalCountries: totals.country_count ?? 0,
    totalDeals: totals.deal_count ?? 0,
    /** Card: prefer incl. estimate blend when BE sends it (Path A). */
    totalValueUSD:
      (totalValueInclEst != null && !Number.isNaN(totalValueInclEst) && totalValueInclEst > 0
        ? totalValueInclEst
        : totalValueUsd != null && !Number.isNaN(totalValueUsd)
          ? totalValueUsd
          : 0) || 0,
    totalValueUSDActualsOnly: totalValueUsd != null && !Number.isNaN(totalValueUsd) ? totalValueUsd : 0,
    top3Vendors,
    topVendorsByDealCount: true,
  };
}

/** Merge raw vendor counts by canonical name (same as client-side market share). */
export function mergeVendorRawCounts(byVendorRaw = [], tenantId, options = {}) {
  const merged = new Map();
  for (const row of byVendorRaw) {
    const raw = row.vendor || row.vendor_name || row.supplier || 'Unknown';
    const canonical = marketShareVendorLabel(
      {
        supplier: raw,
        vendor_state: row.vendor_state,
        incumbent_state: row.incumbent_state,
        incumbent: row.incumbent,
        category: row.category,
      },
      tenantId,
      options,
    );
    const prev = merged.get(canonical) || { vendor: canonical, deal_count: 0 };
    prev.deal_count += row.deal_count ?? 0;
    merged.set(canonical, prev);
  }
  return [...merged.values()].sort((a, b) => b.deal_count - a.deal_count);
}

/** Attach neo estimate fields for DealValueDisplay from server row. */
export function rowNeoEstimateFromServerRow(row) {
  if (row?.neo_estimated_value == null && row?.neo_estimated_value_low == null) {
    return null;
  }
  return {
    estimated_total_usd: row.neo_estimated_value,
    estimated_total_usd_low: row.neo_estimated_value_low,
    estimated_total_usd_high: row.neo_estimated_value_high,
    unit_price_usd_per_student_year: row.neo_unit_price_usd,
    metadata: {
      cohort_level: row.neo_cohort_level,
      reference_count: row.neo_reference_count,
    },
  };
}
